/**
 * Command Groups Registry
 * Central registry for all command groups in MARIA CLI
 */

export const commandGroups = {
  core: [
    'about', 'changelog', 'config', 'contact', 'doctor', 'examples',
    'help', 'history', 'list', 'logout', 'model', 'ping', 'plan',
    'plugins', 'status', 'tutorial', 'usage', 'verify', 'version'
  ],
  auth: ['login', 'logout', 'whoami'],
  code: ['code', 'mm', 'multimodal'],
  configuration: ['config', 'hooks', 'model', 'permissions', 'settings', 'theme', 'setup', 'brain'],
  multimodal: ['face', 'image', 'mm', 'multimodal', 'screenshot', 'video', 'voice'],
  evolution: ['benchmark', 'evolve', 'quality'],
  memory: ['forget', 'memory', 'recall', 'remember', 'memory-status'],
  analysis: ['analyze', 'diagram', 'diff', 'visualize'],
  system: ['doctor', 'health', 'manifest', 'selftest'],
  workflow: ['nl', 'nl-poc', 'workflow/resume'],
  safety: ['check'],
  evaluation: ['run'],
  assistant: ['ask', 'chat', 'think'],
  data: ['db', 'export', 'import', 'sync'],
  utilities: ['alias', 'clear', 'history', 'reset'],
  experimental: ['preview', 'test'],
  business: ['battlecard', 'sales-dashboard', 'tune', 'pilot-setup'],
  plugins: ['plugins'],
  search: ['search'],
  enterprise: ['admin', 'audit', 'compliance', 'security'],
  development: ['debug', 'profile', 'trace', 'calc', 'solve', 'plot']
};

export const allCommands = Object.values(commandGroups).flat();
export const categories = Object.keys(commandGroups);

// Command categories mapping
export const commandCategories = {
  core: 'Core',
  auth: 'Authentication',
  code: 'Code Generation',
  configuration: 'Configuration',
  multimodal: 'Multimodal',
  evolution: 'Evolution',
  memory: 'Memory',
  analysis: 'Analysis',
  system: 'System',
  workflow: 'Workflow',
  safety: 'Safety',
  evaluation: 'Evaluation',
  assistant: 'Assistant',
  data: 'Data',
  utilities: 'Utilities',
  experimental: 'Experimental',
  business: 'Business',
  plugins: 'Plugins',
  search: 'Search',
  enterprise: 'Enterprise',
  development: 'Development'
};

// Command info for type generation - Map each command to its details
export type CommandInfo = {
  name: string;
  category: string;
  aliases?: string[];
  usage?: string;
  examples?: string[];
  deprecated?: { since: string; replacedBy?: string } | true;
};

export const commandInfo: Record<string, CommandInfo> = {};

// Build commandInfo from commandGroups
for (const [category, commands] of Object.entries(commandGroups)) {
  for (const command of commands) {
    const key = `/${command}`;
    commandInfo[key] = {
      name: key,
      category: category,
    };
  }
}

// Add aliases for specific commands
commandInfo['/model'] = { ...commandInfo['/model'], aliases: ['/m', '/models'] };
commandInfo['/memory'] = { ...commandInfo['/memory'], aliases: ['/mem'] };
commandInfo['/login'] = { ...commandInfo['/login'], aliases: ['/signin'] };
commandInfo['/logout'] = { ...commandInfo['/logout'], aliases: ['/signout'] };
commandInfo['/evolve'] = { ...commandInfo['/evolve'], aliases: ['/evolution', '/auto-evolve'] };

// Math commands metadata (development category)
commandInfo['/calc'] = {
  ...commandInfo['/calc'],
  usage: '/calc <expression>',
  examples: ['/calc 2 + 2', '/calc sin(45) * sqrt(16)'],
};
commandInfo['/solve'] = {
  ...commandInfo['/solve'],
  usage: '/solve <equation>',
  examples: ['/solve x^2 + 5x + 6 = 0', '/solve 2x + 3y = 10'],
};
commandInfo['/plot'] = {
  ...commandInfo['/plot'],
  usage: '/plot <function>',
  examples: ['/plot y = x^2', '/plot sin(x) from -pi to pi'],
};

// Deprecated command: /brain
commandInfo['/brain'] = {
  name: '/brain',
  category: 'configuration',
  deprecated: { since: 'v2.5.0', replacedBy: '/mode' },
};

// Helper functions for command information
export function getCommandInfo(command: string): CommandInfo | undefined {
  // Remove leading slash if present
  const raw = command.replace(/^\//, '');
  const lower = raw.toLowerCase();

  // Direct key match (with slash)
  const direct = commandInfo[`/${raw}`];
  if (direct) return direct;

  // Case-insensitive match on names
  for (const [key, info] of Object.entries(commandInfo)) {
    if (key.slice(1).toLowerCase() === lower) return info;
  }

  // Aliases lookup (case-insensitive)
  for (const info of Object.values(commandInfo)) {
    if (info.aliases?.some(a => a.replace(/^\//,'').toLowerCase() === lower)) {
      return { ...info, name: info.name };
    }
  }

  return undefined;
}

export function getRelatedCommands(command: string): CommandInfo[] {
  const info = getCommandInfo(command);
  if (!info) return [];
  
  // Return other commands in the same category
  return Object.entries(commandInfo)
    .filter(([key, cmd]) => cmd.category === info.category && key !== info.name)
    .map(([key, cmd]) => ({
      name: key,
      ...cmd
    }));
}

export function getCommandChain(command: string): string[] {
  // For now, just return the command itself
  // This could be expanded to handle command chains
  return [command];
}

export function getCommandsByCategory(category: string): CommandInfo[] {
  return Object.entries(commandInfo)
    .filter(([_, cmd]) => cmd.category === category)
    .map(([key, cmd]) => ({
      name: key,
      ...cmd
    }));
}

export function suggestCommands(query: string, limit: number = 5): any[] {
  const normalizedQuery = query.toLowerCase().replace('/', '');
  const results: Array<{ command: any; score: number }> = [];
  
  for (const [key, cmd] of Object.entries(commandInfo)) {
    const normalizedKey = key.toLowerCase();
    let score = 0;
    
    // Exact match
    if (normalizedKey === normalizedQuery) {
      score = 100;
    }
    // Starts with query
    else if (normalizedKey.startsWith(normalizedQuery)) {
      score = 80;
    }
    // Contains query
    else if (normalizedKey.includes(normalizedQuery)) {
      score = 60;
    }
    
    if (score > 0) {
      results.push({
        command: { name: key, ...cmd },
        score
      });
    }
  }
  
  // Sort by score and return top results
  return results
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(r => r.command);
}

export function buildHelp(): string {
  const lines: string[] = [];
  // Legend for deprecated commands
  lines.push('※ deprecated: commands marked with [deprecated] will be removed in future releases');
  lines.push('');

  // Category headings with emojis (as asserted in tests)
  const headings: Record<string, string> = {
    core: ' Core Commands',
    utilities: ' Utilities',
  };

  for (const [category, commands] of Object.entries(commandGroups)) {
    const title = headings[category] || `${commandCategories[category as keyof typeof commandCategories] || category}`;
    lines.push(title);
    for (const cmd of commands) {
      const info = commandInfo[`/${cmd}`];
      if (!info) continue;
      const dep = info.deprecated && (info.deprecated as any)?.since
        ? ` [deprecated since ${(info.deprecated as any).since}${(info.deprecated as any).replacedBy ? `; use ${(info.deprecated as any).replacedBy}` : ''}]`
        : (info.deprecated ? ' [deprecated]' : '');
      lines.push(`  /${cmd}${dep}`);
    }
    lines.push('');
  }

  return lines.join('\n');
}

export function assertRegistry(): void {
  // key === name
  for (const [key, info] of Object.entries(commandInfo)) {
    if (key !== info.name) {
      throw new Error(`Key/name mismatch: ${key} !== ${info.name}`);
    }
  }
  // math command docs
  for (const name of ['/calc','/solve','/plot']) {
    const info = commandInfo[name];
    if (!info?.usage || !info?.examples || info.examples.length === 0) {
      throw new Error(`Missing usage/examples for ${name}`);
    }
  }
  // /brain deprecated
  const brain = commandInfo['/brain'];
  if (!brain?.deprecated) {
    throw new Error('/brain must be deprecated');
  }
}
// Workflow: /workflow/resume metadata
commandInfo['/workflow/resume'] = {
  ...commandInfo['/workflow/resume'],
  usage:
    '/workflow/resume [<taskId>] [--latest] [--date YYYY-MM-DD] [--limit N] [--rehydrate summary|full] [--open] [--tests] [--fix] [--apply] [--dry-run]',
  examples: [
    '/workflow/resume --latest',
    '/workflow/resume feat.api.health --tests --fix',
    '/workflow/resume --date 2025-09-04 --limit 5',
  ],
};
